import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Building2, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const Payment = () => {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentInfo();
  }, []);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/payment/info`);
      if (response.data.success) {
        setPaymentInfo(response.data.payment_info);
      }
    } catch (error) {
      console.error('Error fetching payment info:', error);
      toast.error('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  if (loading) {
    return <div className="text-center py-12">Loading payment information...</div>;
  }

  if (!paymentInfo) {
    return <div className="text-center py-12 text-red-500">Failed to load payment information</div>;
  }

  return (
    <div className="space-y-6" data-testid="payment-container">
      <Toaster />
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Payment Information</h1>
        <p className="text-gray-600 mt-1">Share these payment details with your guests</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* UPI Payment Card */}
        <Card className="hover:shadow-lg transition-shadow" data-testid="upi-payment-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="text-green-600" />
              UPI Payment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center bg-white p-4 rounded-lg border">
              <img
                src={paymentInfo.upi_qr_url}
                alt="UPI QR Code"
                className="w-64 h-64 object-contain"
                data-testid="upi-qr-image"
              />
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Scan with any UPI app (Google Pay, PhonePe, Paytm)</p>
            </div>
          </CardContent>
        </Card>

        {/* Bank Details Card */}
        <Card className="hover:shadow-lg transition-shadow" data-testid="bank-details-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="text-blue-600" />
              Bank Transfer Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div
                className="bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => copyToClipboard(paymentInfo.account_holder, 'Account Holder Name')}
              >
                <p className="text-xs text-gray-500 mb-1">Account Holder Name</p>
                <p className="font-semibold text-gray-800" data-testid="account-holder-text">{paymentInfo.account_holder}</p>
              </div>

              <div
                className="bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => copyToClipboard(paymentInfo.account_number, 'Account Number')}
              >
                <p className="text-xs text-gray-500 mb-1">Account Number</p>
                <p className="font-semibold font-mono text-gray-800" data-testid="account-number-text">
                  {paymentInfo.account_number}
                </p>
              </div>

              <div
                className="bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => copyToClipboard(paymentInfo.ifsc_code, 'IFSC Code')}
              >
                <p className="text-xs text-gray-500 mb-1">IFSC Code</p>
                <p className="font-semibold font-mono text-gray-800" data-testid="ifsc-code-text">{paymentInfo.ifsc_code}</p>
              </div>

              <div
                className="bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => copyToClipboard(paymentInfo.bank_name, 'Bank Name')}
              >
                <p className="text-xs text-gray-500 mb-1">Bank Name</p>
                <p className="font-semibold text-gray-800" data-testid="bank-name-text">{paymentInfo.bank_name}</p>
              </div>
            </div>

            <Separator />

            <div className="text-sm text-gray-600 space-y-2">
              <p className="font-semibold text-gray-700">Payment Instructions:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Open your banking app or UPI app</li>
                <li>Scan the QR code or enter the UPI ID</li>
                <li>For bank transfer, use the details above</li>
                <li>Enter the booking amount</li>
                <li>Complete the payment</li>
                <li>Share the transaction reference with us</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-green-50 border-green-200">
        <CardHeader>
          <CardTitle className="text-green-900 flex items-center gap-2">
            <CreditCard className="text-green-700" />
            Payment Methods Accepted
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold text-green-700">Google Pay</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold text-blue-700">PhonePe</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold text-indigo-700">Paytm</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold text-orange-700">Bank Transfer</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact & Social Media Section */}
      {paymentInfo && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Contact Details */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700 mb-3">Get in Touch</h3>
                <div className="space-y-2">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Phone</p>
                    <p className="font-semibold">+91 {paymentInfo.contact_phone}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="font-semibold text-sm">{paymentInfo.contact_email}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Website</p>
                    <a 
                      href={`https://${paymentInfo.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-semibold text-green-700 hover:underline text-sm"
                    >
                      {paymentInfo.website}
                    </a>
                  </div>
                </div>
              </div>

              {/* Facebook QR */}
              <div className="text-center">
                <h3 className="font-semibold text-gray-700 mb-3">Follow Us on Facebook</h3>
                <img 
                  src={paymentInfo.facebook_qr_url}
                  alt="Facebook Page QR"
                  className="w-40 h-40 mx-auto object-contain border rounded-lg"
                />
                <p className="text-xs text-gray-600 mt-2">Scan to follow</p>
              </div>

              {/* Google Location QR */}
              <div className="text-center">
                <h3 className="font-semibold text-gray-700 mb-3">Find Us on Google Maps</h3>
                <img 
                  src={paymentInfo.google_location_qr_url}
                  alt="Google Location QR"
                  className="w-40 h-40 mx-auto object-contain border rounded-lg"
                />
                <p className="text-xs text-gray-600 mt-2">Scan for directions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Payment;